import React from 'react';
import { Question } from '../types';

interface CategoryBreakdownItem {
  name: string;
  correct: number;
  total: number;
  score: number;
}

interface PrintExamDocumentProps {
  examTitle?: string;
  certName?: string;
  vendor?: string;
  questions?: Question[];
  userAnswers?: Record<string, string>;
  isSubmitted?: boolean;
  score?: number;
  passed?: boolean;
  timeSpentMinutes?: number;
  categoryBreakdown?: CategoryBreakdownItem[];
}

export const PrintExamDocument: React.FC<PrintExamDocumentProps> = ({
  examTitle = "Standard Practice Examination Simulation",
  certName = "Nurse Aide Certification (CNA)",
  vendor = "Registry Curated",
  questions = [],
  userAnswers = {},
  isSubmitted = false,
  score = 0,
  passed = false,
  timeSpentMinutes,
  categoryBreakdown = []
}) => {
  const currentDateFormatted = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div id="print-exam-document" className="hidden print:block w-full bg-white text-slate-900 p-0 m-0 font-sans leading-relaxed">
      {/* HEADER / COVER BLOCK */}
      <div className="p-8 mb-6 border-b-4 border-slate-900">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-[10px] font-black tracking-[0.2em] text-slate-500 uppercase mb-1">
              DIAMOND SCHOOL OF NURSING & CLINICAL REGISTRY HUB
            </p>
            <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
              {isSubmitted ? 'Practice Examination Evaluation & Rationale' : 'Official Board Examination Question Packet'}
            </h1>
            <p className="text-xs font-bold text-indigo-700 mt-1.5 tracking-wide uppercase">
              {certName} — {examTitle} ({vendor})
            </p>
          </div>
          <div className="text-right border-l-2 border-slate-200 pl-4 py-1">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Board Simulation</p>
            <p className="text-[10px] font-bold text-slate-700 mt-0.5">{questions.length} Questions</p>
            <p className="text-[9px] text-slate-500 mt-0.5">{currentDateFormatted}</p>
          </div>
        </div>

        {/* CANDIDATE INFORMATION & EVALUATION BLOCK */}
        <div className="mt-6 pt-5 border-t border-slate-200 grid grid-cols-3 gap-6 text-[10px] font-bold text-slate-700">
          <div>
            <span className="text-slate-400 block text-[8px] uppercase tracking-widest font-black">Candidate Name</span>
            <div className="border-b border-slate-400 h-6 mt-1" />
          </div>
          <div>
            <span className="text-slate-400 block text-[8px] uppercase tracking-widest font-black">Registry ID / Testing State</span>
            <div className="border-b border-slate-400 h-6 mt-1" />
          </div>
          <div>
            <span className="text-slate-400 block text-[8px] uppercase tracking-widest font-black">Proctor / Instructor Sign-Off</span>
            <div className="border-b border-slate-400 h-6 mt-1" />
          </div>
        </div>
      </div>

      {/* SUBMITTED / COMPLETED EXAM PERFORMANCE SUMMARY */}
      {isSubmitted && (
        <div className="mb-8 px-4 print-no-cut">
          <div className="p-6 border-2 border-slate-900 rounded-2xl bg-slate-50 mb-6 flex justify-between items-center">
            <div>
              <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Simulation Result</span>
              <h2 className="text-2xl font-black text-slate-900 mt-0.5">
                Overall Score: {score}%
              </h2>
              <p className="text-xs font-semibold text-slate-600 mt-1">
                Passing Threshold: 75% {timeSpentMinutes ? `• Duration: ${timeSpentMinutes} mins` : ''}
              </p>
            </div>
            <div className="text-right">
              <span className={`inline-block px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest border-2 ${
                passed 
                  ? 'bg-emerald-100 text-emerald-900 border-emerald-600' 
                  : 'bg-rose-100 text-rose-900 border-rose-600'
              }`}>
                {passed ? '★ PASSED BOARD SIMULATION' : 'REQUIRES REMEDIATION'}
              </span>
            </div>
          </div>

          {/* TOPIC BREAKDOWN TABLE */}
          {categoryBreakdown.length > 0 && (
            <div className="mb-6">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-900 mb-2">
                Domain & Category Performance Breakdown
              </h3>
              <table className="w-full text-left text-xs border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100 text-slate-900 border-b border-slate-300 font-black uppercase text-[9px] tracking-wider">
                    <th className="p-2 border border-slate-300">Domain Category</th>
                    <th className="p-2 border border-slate-300 text-center">Correct / Total</th>
                    <th className="p-2 border border-slate-300 text-center">Percentage Score</th>
                    <th className="p-2 border border-slate-300 text-center">Proficiency Level</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryBreakdown.map((cat, i) => (
                    <tr key={i} className="border-b border-slate-200">
                      <td className="p-2 border border-slate-300 font-bold">{cat.name}</td>
                      <td className="p-2 border border-slate-300 text-center font-semibold">{cat.correct} / {cat.total}</td>
                      <td className="p-2 border border-slate-300 text-center font-bold">{cat.score}%</td>
                      <td className="p-2 border border-slate-300 text-center font-bold text-[10px]">
                        {cat.score >= 80 ? 'EXCELLENT' : cat.score >= 75 ? 'PASSING' : 'NEEDS STUDY'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* UNCOMPLETED EXAM INSTRUCTIONS HEADER */}
      {!isSubmitted && (
        <div className="mb-6 px-4 p-4 bg-slate-50 border border-slate-300 rounded-xl text-xs font-medium text-slate-800 print-no-cut">
          <p className="font-bold uppercase tracking-wide text-slate-900 mb-1">Candidate Instructions:</p>
          <p>
            Read each question carefully and select the single best answer. Mark your response in the provided choice boxes.
            This practice examination is designed to simulate standard state and national registry certification formats.
          </p>
        </div>
      )}

      {/* QUESTIONS CATALOG */}
      <div className="space-y-6 px-4">
        <h2 className="text-sm font-black uppercase tracking-widest text-slate-900 border-b-2 border-slate-900 pb-2 mb-4">
          {isSubmitted ? 'Questions, Selected Answers & Rationales' : 'Examination Questions'}
        </h2>

        {questions.map((q, idx) => {
          const userAnsVal = userAnswers[q._id];
          const options = q.options || [];
          
          return (
            <div key={q._id || idx} className="p-5 border border-slate-300 rounded-2xl bg-white print-no-cut space-y-3">
              {/* Question Header */}
              <div className="flex justify-between items-center border-b border-slate-200 pb-2">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-900">
                  Question {idx + 1} of {questions.length}
                </span>
                <div className="flex items-center gap-2">
                  {q.category && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[8px] font-bold uppercase rounded border border-slate-300">
                      {q.category}
                    </span>
                  )}
                  {q.difficulty && (
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-[8px] font-bold uppercase rounded border border-slate-300">
                      {q.difficulty}
                    </span>
                  )}
                </div>
              </div>

              {/* Question Body */}
              <p className="text-xs font-bold text-slate-900 leading-relaxed">
                {q.text}
              </p>

              {/* Question Image / Diagram */}
              {(q.imageUrl || (q as any).image) && (
                <div className="my-2 text-center print-no-cut">
                  <img
                    src={q.imageUrl || (q as any).image}
                    alt="Question Diagram"
                    className="print-image-fit max-h-[250px] object-contain mx-auto rounded-lg border border-slate-300"
                  />
                </div>
              )}

              {/* Options List */}
              <div className="space-y-2 pt-1">
                {options.map((opt, oIdx) => {
                  const optVal = opt._id || oIdx.toString();
                  const isUserChoice = userAnsVal === optVal;
                  const isCorrect = opt.isCorrect;

                  let optClass = "p-2.5 rounded-xl border text-xs font-medium flex items-start gap-3 ";
                  if (isSubmitted) {
                    if (isCorrect) {
                      optClass += "bg-emerald-50 border-emerald-400 text-emerald-950 font-bold";
                    } else if (isUserChoice && !isCorrect) {
                      optClass += "bg-rose-50 border-rose-400 text-rose-950 font-bold";
                    } else {
                      optClass += "bg-white border-slate-200 text-slate-600 opacity-70";
                    }
                  } else {
                    optClass += "bg-white border-slate-300 text-slate-900";
                  }

                  return (
                    <div key={optVal} className={optClass}>
                      <div className="font-bold text-[10px] min-w-[20px] text-center pt-0.5">
                        [{String.fromCharCode(65 + oIdx)}]
                      </div>
                      <div className="flex-1 leading-snug">
                        {opt.text}
                      </div>
                      {isSubmitted && (
                        <div className="text-[9px] font-black uppercase shrink-0 pt-0.5">
                          {isCorrect && (
                            <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded">
                              ✓ Correct Answer
                            </span>
                          )}
                          {isUserChoice && !isCorrect && (
                            <span className="text-rose-700 bg-rose-100 px-2 py-0.5 rounded ml-1">
                              ✗ Your Answer
                            </span>
                          )}
                          {isUserChoice && isCorrect && (
                            <span className="text-emerald-800 bg-emerald-200 px-2 py-0.5 rounded ml-1">
                              Your Answer
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Rationale Section (Show if submitted or review) */}
              {(isSubmitted || q.explanation) && (
                <div className="mt-3 p-3 bg-indigo-50/60 border border-indigo-200 rounded-xl text-[11px] text-slate-800 space-y-1">
                  <span className="font-black text-indigo-900 text-[9px] uppercase tracking-wider block">
                    Clinical Rationale & Board Logic:
                  </span>
                  <p className="leading-relaxed text-slate-700 font-medium">
                    {q.explanation || "This question evaluates standard board clinical decision making and patient care priorities."}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* FOOTER BLOCK */}
      <div className="mt-12 pt-6 border-t-2 border-slate-900 text-center text-[9px] font-bold text-slate-400 uppercase tracking-widest print-no-cut">
        Diamond School of Nursing & Clinical Registry Hub — End of Examination Material
      </div>
    </div>
  );
};
